import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useUI } from '@/hooks/ui/useUI';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { useTranslation } from '@/i18n';
import { ROUTE_DEFINITIONS } from '@/routes';
import { shiftService } from '@/api/domains/shift';
import type { ScheduleOption, LC_ShiftDayDetailIn, LC_CreateCompanyShiftIn } from '@/api/domains/shift';
import type { WorkingDayItem } from '@/api/domains/workingDay';
import { WORKING_DAY_TYPE_ID } from '@/api/domains/catalog/workingDayType';
import type { HourCounterBadgeVariant } from '@/components/ui/badges/HourCounterBadge';
import {
  createShiftDescriptionSchema,
  type CreateShiftDescriptionForm,
} from '@/schemas/shift/createShiftDescriptionForm';
import type {
  AssignScheduleReplicableDay,
  AssignScheduleExistingAssignment,
  AssignScheduleResult,
} from '@/hooks/shift/useAssignScheduleFormPanel';


const SCHEDULE_CATALOG_PAGE_SIZE = 1000;
const DAYS_PER_WEEK = 7;

// ─── Legal thresholds mirrored from backend CompanyShiftConstants (ASIS-5038) ──
const WEEKLY_HOURS_WARNING_RATIO = 0.95;
const CYCLE_WEEKLY_AVERAGE_WARNING_THRESHOLD = 40;
const MAX_CYCLE_WEEKLY_AVERAGE_HOURS = 45;
// Tolerance for float comparisons against the dynamic Biweekly ceiling
// (days ÷ 7 × progresividad rarely lands on a whole number).
const HOURS_EQUALITY_EPSILON = 0.01;

const DAY_LABEL_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
type DayLabelKey = typeof DAY_LABEL_KEYS[number];

const DISTRIBUTED_OR_EXCEPTIONAL_TYPES: number[] = [
  WORKING_DAY_TYPE_ID.DISTRIBUTED_CYCLES,
  WORKING_DAY_TYPE_ID.EXCEPTIONAL,
];

interface DayAssignment {
  schedule1Id: number | null;
  schedule2Id: number | null;
}

export interface AssignedScheduleView {
  id: number;
  name: string;
  startTime: string;
  endTime: string;
  breakMinutes: number;
  netHours: number;
}

export interface ShiftDayCellViewModel {
  daySequence: number;
  weekNumber: number;
  dayLabelKey: DayLabelKey | null;
  dayNumberLabel: string;
  isOffPattern: boolean;
  schedule1: AssignedScheduleView | null;
  schedule2: AssignedScheduleView | null;
}

export interface HourCounterViewModel {
  value: string;
  variant: HourCounterBadgeVariant;
  /** Texto de alerta mostrado junto al badge cuando el estado requiere una explicación
   * adicional (ASIS-5053 QA: PHSC en amarillo/rojo, bloque Bisemanal en rojo). */
  warningMessage?: string;
}

export interface ShiftWeekViewModel {
  weekNumber: number;
  days: ShiftDayCellViewModel[];
  counter: HourCounterViewModel | null;
}

export interface UseShiftWeeklyDistributionParams {
  descriptionValues: CreateShiftDescriptionForm;
  selectedWorkingDay: WorkingDayItem | undefined;
  ordinaryHours: number | null;
  /**
   * Marks the pending navigation as confirmed on `useUnsavedChangesWarning` before
   * redirecting after a successful save — otherwise the "Cambios sin guardar" prompt
   * fires even though the shift was already persisted.
   */
  confirmNavigation?: () => void;
  /**
   * ASIS-5045 (Edit) — preloads the grid with the shift's existing day assignments.
   * Omitted by Create, which always starts from an empty grid.
   */
  initialDetails?: LC_ShiftDayDetailIn[];
  /**
   * ASIS-5045 (Edit) — when true, disables all per-day interactivity (no
   * AssignScheduleFormPanel, no Save button) without hiding any information.
   * Omitted/false by Create.
   */
  readOnly?: boolean;
  /**
   * ASIS-5045 (Edit) — when provided, the grid's own Save button delegates entirely
   * to this handler instead of building/POSTing its own payload, passing it the
   * current day assignments. The combined save (description form + day assignments)
   * is centralized in `useShiftEdit.onSave`. Omitted by Create, which keeps its own
   * POST logic unchanged.
   */
  onSaveOverride?: (details: LC_ShiftDayDetailIn[]) => Promise<void>;
  /**
   * ASIS-5045 (Edit) — when `onSaveOverride` is used, the real saving/loading state
   * lives in `useShiftEdit`; pass it through so the grid's Save button reflects it
   * instead of tracking a redundant local `isSaving` flag.
   */
  externalIsSaving?: boolean;
}

export interface UseShiftWeeklyDistributionResult {
  weeks: ShiftWeekViewModel[];
  showWeekHeaders: boolean;
  cycleCounter: HourCounterViewModel | null;
  isSaveDisabled: boolean;
  isSaving: boolean;
  isReadOnly: boolean;
  openAssignPanel: (daySequence: number) => void;
  onSave: () => Promise<void>;
  /** Current day-distribution details, derived from the in-grid assignments state. */
  details: LC_ShiftDayDetailIn[];
}

const formatHoursMinutes = (totalHours: number): string => {
  const safeHours = Math.max(totalHours, 0);
  const wholeHours = Math.floor(safeHours);
  const minutes = Math.round((safeHours - wholeHours) * 60);
  return `${wholeHours}:${String(minutes).padStart(2, '0')}`;
};

const parseTimeToMinutes = (time: string): number => {
  const [hoursPart, minutesPart] = time.split(':');
  return Number(hoursPart) * 60 + Number(minutesPart ?? 0);
};

const computeNetHours = (schedule: ScheduleOption): number => {
  const start = parseTimeToMinutes(schedule.startTime);
  let end = parseTimeToMinutes(schedule.endTime);
  if (end < start) end += 24 * 60;
  const grossMinutes = end - start;
  return (grossMinutes - schedule.breakMinutes) / 60;
};

const dayNumberLabel = (daySequence: number): string =>
  daySequence <= DAYS_PER_WEEK ? `D${daySequence}` : `D${String(daySequence).padStart(2, '0')}`;

const sumWeeksHours = (
  weeks: ShiftWeekViewModel[],
  dayHours: (daySequence: number) => number,
): number => weeks.reduce(
  (sum, week) => sum + week.days.reduce((weekSum, day) => weekSum + dayHours(day.daySequence), 0),
  0,
);

/**
 * Groups a flat `LC_ShiftDayDetailIn[]` (up to 2 entries per day: schedule1/schedule2,
 * or a single `scheduleId: null` entry for off-pattern days) back into the
 * per-day assignments map used by the grid. Used to preload Edit with the shift's
 * existing distribution.
 */
const buildInitialAssignments = (
  details: LC_ShiftDayDetailIn[] | undefined,
): Map<number, DayAssignment> => {
  const map = new Map<number, DayAssignment>();
  if (!details) return map;
  details.forEach((detail) => {
    if (detail.scheduleId === null) return;
    const existing = map.get(detail.daySequence);
    if (!existing) {
      map.set(detail.daySequence, { schedule1Id: detail.scheduleId, schedule2Id: null });
    } else if (existing.schedule2Id === null) {
      map.set(detail.daySequence, { ...existing, schedule2Id: detail.scheduleId });
    }
  });
  return map;
};

export function useShiftWeeklyDistribution({
  descriptionValues,
  selectedWorkingDay,
  ordinaryHours,
  confirmNavigation,
  initialDetails,
  readOnly = false,
  onSaveOverride,
  externalIsSaving,
}: UseShiftWeeklyDistributionParams): UseShiftWeeklyDistributionResult {
  const { t: translate } = useTranslation('schedule');
  const { showNotification, showSuccess, showApiError, openSidePanel } = useUI();
  const navigate = useNavigate();

  const [assignments, setAssignments] = useState<Map<number, DayAssignment>>(new Map());
  const [isSavingInternal, setIsSavingInternal] = useState(false);
  const isSaving = externalIsSaving ?? isSavingInternal;

  // Preload the grid with the shift's existing distribution (Edit) once it's available —
  // the shift detail loads asynchronously, so this can't be a lazy useState initializer.
  const initialDetailsAppliedRef = useRef(false);
  useEffect(() => {
    if (initialDetailsAppliedRef.current) return;
    if (initialDetails && initialDetails.length > 0) {
      setAssignments(buildInitialAssignments(initialDetails));
      initialDetailsAppliedRef.current = true;
    }
  }, [initialDetails]);

  // ASIS-5051 (perf): loaded once here, passed down into AssignScheduleFormPanel's
  // props on every openAssignPanel call — it used to re-fetch this same catalog on
  // every day-card open, since SidePanelContainer unmounts the panel on close.
  const {
    data: schedules,
    options: scheduleOptions,
    isLoaded: isSchedulesLoaded,
  } = useCatalogLoader(
    async (signal) => {
      const response = await shiftService.getSchedules(
        { pageSize: SCHEDULE_CATALOG_PAGE_SIZE },
        signal,
      );
      if (!response.success || !response.data) return { ...response, data: null };
      return { ...response, data: response.data.items };
    },
    (item: ScheduleOption) => ({
      value: String(item.id),
      label: `${item.name} (${item.startTime} - ${item.endTime})`,
    }),
    [],
    false,
    () => showNotification(
      'error',
      translate('shift.create.assignSchedule.messages.loadSchedulesError'),
    ),
  );

  const { workingDayTypeId, workingDayId, weeksCount } = descriptionValues;

  // Changing tipo/jornada after the grid already has a real baseline invalidates the
  // current day assignments (different cycle length/pattern) — clear them so Guardar
  // requires re-assigning at least one horario (ASIS-5054, PO feedback). Guarded on
  // "previous value was not null" so it never fires on the initial null→value transition
  // (Create's first selection, or Edit's async preload of the shift's own values).
  const previousWorkingDayTypeIdRef = useRef(workingDayTypeId);
  const previousWorkingDayIdRef = useRef(workingDayId);
  useEffect(() => {
    const previousWorkingDayTypeId = previousWorkingDayTypeIdRef.current;
    const previousWorkingDayId = previousWorkingDayIdRef.current;
    previousWorkingDayTypeIdRef.current = workingDayTypeId;
    previousWorkingDayIdRef.current = workingDayId;

    const typeChanged =
      previousWorkingDayTypeId !== null && previousWorkingDayTypeId !== workingDayTypeId;
    const workingDayChanged =
      previousWorkingDayId !== null && previousWorkingDayId !== workingDayId;
    if (typeChanged || workingDayChanged) {
      setAssignments(new Map());
    }
  }, [workingDayTypeId, workingDayId]);

  const isDistributedOrExceptional =
    workingDayTypeId !== null && DISTRIBUTED_OR_EXCEPTIONAL_TYPES.includes(workingDayTypeId);
  const showWeekHeaders = !isDistributedOrExceptional;
  const isBiweekly = workingDayTypeId === WORKING_DAY_TYPE_ID.BIWEEKLY;

  const totalDays = weeksCount * DAYS_PER_WEEK;

  // ── Off-pattern (auto "Libre") days — only Distribuida por ciclos / Excepcional ──
  const isOffPatternDay = useCallback(
    (daySequence: number): boolean => {
      if (!isDistributedOrExceptional) return false;
      const workingDaysPattern = selectedWorkingDay?.workingDays;
      const offDaysPattern = selectedWorkingDay?.offDays;
      if (!workingDaysPattern || !offDaysPattern) return false;
      const cycleLength = workingDaysPattern + offDaysPattern;
      const positionInCycle = (daySequence - 1) % cycleLength;
      return positionInCycle >= workingDaysPattern;
    },
    [isDistributedOrExceptional, selectedWorkingDay],
  );

  const findSchedule = useCallback(
    (id: number | null): ScheduleOption | null => {
      if (id === null) return null;
      return schedules.find((schedule) => schedule.id === id) ?? null;
    },
    [schedules],
  );

  const toAssignedScheduleView = useCallback(
    (id: number | null): AssignedScheduleView | null => {
      const schedule = findSchedule(id);
      if (!schedule) return null;
      return { ...schedule, netHours: computeNetHours(schedule) };
    },
    [findSchedule],
  );

  const dayHours = useCallback(
    (daySequence: number): number => {
      const assignment = assignments.get(daySequence);
      if (!assignment) return 0;
      const first = toAssignedScheduleView(assignment.schedule1Id);
      const second = toAssignedScheduleView(assignment.schedule2Id);
      return (first?.netHours ?? 0) + (second?.netHours ?? 0);
    },
    [assignments, toAssignedScheduleView],
  );

  // ── Week/day view model construction ──────────────────────────────────────
  const weeks: ShiftWeekViewModel[] = useMemo(() => {
    const result: ShiftWeekViewModel[] = [];

    for (let weekIndex = 0; weekIndex < weeksCount; weekIndex += 1) {
      const weekNumber = weekIndex + 1;
      const days: ShiftDayCellViewModel[] = [];
      let weekTotalHours = 0;

      for (let position = 0; position < DAYS_PER_WEEK; position += 1) {
        const daySequence = weekIndex * DAYS_PER_WEEK + position + 1;
        const assignment = assignments.get(daySequence);
        weekTotalHours += dayHours(daySequence);

        days.push({
          daySequence,
          weekNumber,
          dayLabelKey: isDistributedOrExceptional ? null : DAY_LABEL_KEYS[position],
          dayNumberLabel: dayNumberLabel(daySequence),
          isOffPattern: isOffPatternDay(daySequence),
          schedule1: assignment ? toAssignedScheduleView(assignment.schedule1Id) : null,
          schedule2: assignment ? toAssignedScheduleView(assignment.schedule2Id) : null,
        });
      }

      let counter: ShiftWeekViewModel['counter'] = null;
      if (!isDistributedOrExceptional && !isBiweekly && ordinaryHours !== null) {
        const target = ordinaryHours;
        let variant: HourCounterBadgeVariant = 'info';
        if (weekTotalHours > target) variant = 'error';
        else if (weekTotalHours === target) variant = 'success';
        else if (weekTotalHours >= target * WEEKLY_HOURS_WARNING_RATIO) variant = 'warning';
        counter = {
          value: translate('shift.create.fields.hourCounterRatio', {
            current: formatHoursMinutes(weekTotalHours),
            target: formatHoursMinutes(target),
          }),
          variant,
        };
      }

      result.push({ weekNumber, days, counter });
    }

    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    weeksCount, assignments, dayHours, isDistributedOrExceptional,
    isOffPatternDay, ordinaryHours, isBiweekly, translate,
  ]);

  // ── Biweekly block-total counter (ASIS-5038: techo dinámico = días totales del
  // ciclo ÷ 7 × progresividad vigente — confirmado por negocio 2026-07-17 y
  // verificado contra NE_ShiftLegalRulesEvaluator.EvaluateBiweeklyLimit) ──
  const biweeklyBlockCounter = useMemo(() => {
    if (!isBiweekly || ordinaryHours === null) return null;
    const totalHours = sumWeeksHours(weeks, dayHours);
    let variant: HourCounterBadgeVariant = 'info';
    if (totalHours > ordinaryHours + HOURS_EQUALITY_EPSILON) variant = 'error';
    else if (Math.abs(totalHours - ordinaryHours) < HOURS_EQUALITY_EPSILON) variant = 'success';
    return {
      value: translate('shift.create.fields.hourCounterRatio', {
        current: formatHoursMinutes(totalHours),
        target: formatHoursMinutes(ordinaryHours),
      }),
      variant,
      warningMessage: variant === 'error'
        ? translate('shift.create.fields.biweeklyBlockExceededWarning', {
          max: formatHoursMinutes(ordinaryHours),
        })
        : undefined,
    };
  }, [isBiweekly, ordinaryHours, weeks, dayHours, translate]);

  // Block-total counter is only meaningful once — rendered on week 1 (matches design).
  const weeksWithBlockCounter: ShiftWeekViewModel[] = useMemo(() => {
    if (!isBiweekly) return weeks;
    return weeks.map((week) => (
      week.weekNumber === 1 ? { ...week, counter: biweeklyBlockCounter } : week
    ));
  }, [isBiweekly, weeks, biweeklyBlockCounter]);

  // ── PHSC counter (Distribuida por ciclos / Excepcional) ─────────────────────
  const cycleCounter = useMemo(() => {
    if (!isDistributedOrExceptional || totalDays === 0) return null;
    const totalCycleHours = sumWeeksHours(weeks, dayHours);
    const phsc = (totalCycleHours / totalDays) * DAYS_PER_WEEK;
    let variant: HourCounterBadgeVariant = 'success';
    if (phsc > MAX_CYCLE_WEEKLY_AVERAGE_HOURS) variant = 'error';
    else if (phsc > CYCLE_WEEKLY_AVERAGE_WARNING_THRESHOLD) variant = 'warning';
    const phscLabel = formatHoursMinutes(phsc);
    let warningMessage: string | undefined;
    if (variant === 'error') {
      warningMessage = translate('shift.create.fields.cycleAverageExceededWarning', { value: phscLabel });
    } else if (variant === 'warning') {
      warningMessage = translate('shift.create.fields.cycleAverageWarning', { value: phscLabel });
    }
    return {
      value: translate('shift.create.fields.hourCounterRatio', {
        current: phscLabel,
        target: formatHoursMinutes(ordinaryHours ?? 0),
      }),
      variant,
      warningMessage,
    };
  }, [isDistributedOrExceptional, totalDays, weeks, dayHours, ordinaryHours, translate]);

  // ── Save gating (FE-011) ────────────────────────────────────────────────────
  const hasRedCounter = useMemo(() => {
    const weekHasError = weeksWithBlockCounter.some((week) => week.counter?.variant === 'error');
    const cycleHasError = cycleCounter?.variant === 'error';
    return weekHasError || cycleHasError;
  }, [weeksWithBlockCounter, cycleCounter]);

  const isDtSectionValid = useMemo(
    () => createShiftDescriptionSchema.safeParse(descriptionValues).success,
    [descriptionValues],
  );

  // At least one working day must have a schedule assigned — otherwise the turno would
  // save with an empty distribution (ASIS-5054, PO feedback). Off-pattern ("Libre") days
  // never take an assignment, so they're excluded from the check.
  const hasAtLeastOneAssignment = useMemo(() => {
    for (let daySequence = 1; daySequence <= totalDays; daySequence += 1) {
      if (isOffPatternDay(daySequence)) continue;
      if (assignments.get(daySequence)?.schedule1Id != null) return true;
    }
    return false;
  }, [totalDays, isOffPatternDay, assignments]);

  const isSaveDisabled = hasRedCounter || !isDtSectionValid || !hasAtLeastOneAssignment || readOnly;

  // ── Panel wiring (FE-010) ────────────────────────────────────────────────────
  const buildReplicableDays = useCallback(
    (excludeDaySequence: number): AssignScheduleReplicableDay[] => {
      const replicable: AssignScheduleReplicableDay[] = [];
      for (let weekIndex = 0; weekIndex < weeksCount; weekIndex += 1) {
        for (let position = 0; position < DAYS_PER_WEEK; position += 1) {
          const daySequence = weekIndex * DAYS_PER_WEEK + position + 1;
          if (daySequence === excludeDaySequence || isOffPatternDay(daySequence)) continue;
          const label = isDistributedOrExceptional
            ? dayNumberLabel(daySequence)
            : `${translate(`shift.create.fields.days.${DAY_LABEL_KEYS[position]}`)} ${dayNumberLabel(daySequence)}`;
          replicable.push({ daySequence, label });
        }
      }
      return replicable;
    },
    [weeksCount, isOffPatternDay, isDistributedOrExceptional, translate],
  );

  const applyAssignment = useCallback((daySequence: number, result: AssignScheduleResult) => {
    setAssignments((previous) => {
      const next = new Map(previous);
      const value: DayAssignment = {
        schedule1Id: result.schedule1Id,
        schedule2Id: result.schedule2Id,
      };
      next.set(daySequence, value);
      result.replicateDaySequences.forEach((targetDaySequence) => {
        next.set(targetDaySequence, { ...value });
      });
      return next;
    });
  }, []);

  const clearAssignment = useCallback((daySequence: number) => {
    setAssignments((previous) => {
      const next = new Map(previous);
      next.delete(daySequence);
      return next;
    });
  }, []);

  const openAssignPanel = useCallback(
    (daySequence: number) => {
      if (readOnly) return;
      const week = Math.floor((daySequence - 1) / DAYS_PER_WEEK) + 1;
      const positionInWeek = (daySequence - 1) % DAYS_PER_WEEK;
      const title = isDistributedOrExceptional
        ? translate('shift.create.assignSchedule.title', { context: dayNumberLabel(daySequence) })
        : translate('shift.create.assignSchedule.title', {
          context: `${translate(`shift.create.fields.days.${DAY_LABEL_KEYS[positionInWeek]}`)} - ${translate('shift.create.assignSchedule.week', { week })} - ${dayNumberLabel(daySequence)}`,
        });

      const existing = assignments.get(daySequence);
      const existingAssignment: AssignScheduleExistingAssignment | undefined = existing
        ? { schedule1Id: existing.schedule1Id, schedule2Id: existing.schedule2Id }
        : undefined;

      openSidePanel({
        title,
        component: 'AssignScheduleFormPanel',
        props: {
          replicableDays: buildReplicableDays(daySequence),
          existingAssignment,
          schedules,
          scheduleOptions,
          isSchedulesLoaded,
          onAssign: (result: AssignScheduleResult) => applyAssignment(daySequence, result),
          onClear: existingAssignment ? () => clearAssignment(daySequence) : undefined,
        },
      });
    },
    [
      readOnly, isDistributedOrExceptional, translate, assignments,
      openSidePanel, buildReplicableDays, applyAssignment, clearAssignment,
      schedules, scheduleOptions, isSchedulesLoaded,
    ],
  );

  // ── Day-distribution details, derived from the current assignments ─────────
  // Exposed on the return object so Edit can combine it with the description
  // form's own fields into the full update payload (see useShiftEdit.onSave).
  const details = useMemo<LC_ShiftDayDetailIn[]>(() => {
    const result: LC_ShiftDayDetailIn[] = [];
    for (let daySequence = 1; daySequence <= totalDays; daySequence += 1) {
      if (isOffPatternDay(daySequence)) {
        result.push({ daySequence, scheduleId: null });
        continue;
      }
      const assignment = assignments.get(daySequence);
      if (!assignment || assignment.schedule1Id === null) {
        result.push({ daySequence, scheduleId: null });
        continue;
      }
      result.push({ daySequence, scheduleId: assignment.schedule1Id });
      if (assignment.schedule2Id !== null) {
        result.push({ daySequence, scheduleId: assignment.schedule2Id });
      }
    }
    return result;
  }, [totalDays, isOffPatternDay, assignments]);

  // ── Save (FE-009/010/011 → POST /v1/attendance/shifts) ──────────────────────
  const navigateToList = useCallback(() => {
    void navigate(ROUTE_DEFINITIONS.TURNOS_ROTATIVOS_FIJOS.path);
  }, [navigate]);

  const onSave = useCallback(async () => {
    if (onSaveOverride) {
      await onSaveOverride(details);
      return;
    }

    const payload: LC_CreateCompanyShiftIn = {
      workingDayTypeId: descriptionValues.workingDayTypeId as number,
      workingDayId: descriptionValues.workingDayId as number,
      shiftCode: descriptionValues.shiftCode,
      shiftName: descriptionValues.shiftName,
      weeksCount: descriptionValues.weeksCount,
      isExceptionalWorkingDay: descriptionValues.isExceptionalWorkingDay,
      dtAuthorizationNumber: descriptionValues.dtAuthorizationNumber || undefined,
      dtAuthorizationDate: descriptionValues.dtAuthorizationDate || undefined,
      staticStartDate: descriptionValues.staticStartDate || undefined,
      details,
    };

    setIsSavingInternal(true);
    try {
      const response = await shiftService.createCompanyShift(payload);

      if (!response.success) {
        if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
          const message = response.error.validationErrors.map((error) => error.errorMessage).join(' ');
          showNotification('error', message);
          return;
        }
        showApiError(response, translate('shift.create.messages.createError'));
        return;
      }

      showSuccess(translate('shift.create.messages.createSuccess'));
      // Mark navigation as confirmed BEFORE redirecting — the shift is already saved,
      // so the "Cambios sin guardar" blocker (still armed because the Descripción form
      // stays dirty) must not intercept this programmatic navigation.
      confirmNavigation?.();
      navigateToList();
    } finally {
      setIsSavingInternal(false);
    }
  }, [
    onSaveOverride,
    details,
    descriptionValues,
    showNotification,
    showApiError,
    showSuccess,
    translate,
    confirmNavigation,
    navigateToList,
  ]);

  return {
    weeks: weeksWithBlockCounter,
    showWeekHeaders,
    cycleCounter,
    isSaveDisabled,
    isSaving,
    isReadOnly: readOnly,
    openAssignPanel,
    onSave,
    details,
  };
}
